import { Button, DialogContent, DialogTitle } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { notification } from '@/notifications/notification-store'

export interface ApiKeyRevealDialogProps {
	open: boolean
	/** The key's label, so the user can tell which one this is. */
	name: string
	/** The plaintext. Held only for as long as this dialog is open. */
	plaintextKey: string
	onClose: () => void
}

/**
 * The one and only view of a plaintext key.
 *
 * It is not stored, cached or re-fetchable — the server keeps a hash — so closing
 * this dialog destroys the only copy the user has. That is why the wording is
 * explicit rather than reassuring, and why closing takes a deliberate press:
 * Escape and backdrop dismissal are disabled, unlike every other dialog in the app.
 *
 * Presentational: it receives the key rather than fetching it, so it never holds a
 * credential beyond its own lifetime.
 */
export function ApiKeyRevealDialog({ open, name, plaintextKey, onClose }: ApiKeyRevealDialogProps) {
	const [copied, setCopied] = useState(false)

	// Reset between openings, so a second key does not open already showing "Copied".
	useEffect(() => {
		if (open) setCopied(false)
	}, [open])

	if (!open) return null

	// `void`-returning, not async: `onPress` expects void, and a floating promise
	// here would escape the try/catch that turns a denied clipboard into guidance.
	function handleCopy(): void {
		navigator.clipboard
			.writeText(plaintextKey)
			.then(() => setCopied(true))
			.catch(() => {
				// A denied clipboard permission is the common case, and the key is on
				// screen to select manually — guidance, not a failed creation.
				notification.warning('Could not copy automatically — select the key and copy it manually')
			})
	}

	return (
		// Escape stays disabled with no pending mutation behind it, which is the one
		// case the kit's contract does not cover: the key is shown exactly once and
		// never again, so a stray Escape costs the user the credential. Dismissal is
		// deliberate here, through the button that acknowledges that.
		<DialogContent size="lg" isOpen={open} isKeyboardDismissDisabled>
			<DialogTitle>Copy your API key</DialogTitle>
			<p className="mt-2 text-ds-sm leading-5 text-ds-text-muted">
				This is the only time <span className="font-medium text-ds-text">{name}</span> can be shown. Only a hash is
				stored, so it cannot be recovered — if you lose it, create a new key and revoke this one.
			</p>

			{/* `readOnly` rather than a <code> block: the value must be selectable and
				    reachable by a screen reader as a value, not as decoration. */}
			<label className="mt-4 block" htmlFor="api-key-plaintext">
				<span className="sr-only">Your new API key</span>
				<input
					id="api-key-plaintext"
					readOnly
					value={plaintextKey}
					onFocus={(event) => event.currentTarget.select()}
					className="w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 font-ds-mono text-ds-sm text-ds-text"
				/>
			</label>

			<div className="mt-6 flex items-center justify-between gap-2">
				<Button type="button" variant="secondary" className="rounded-ds-full" onPress={handleCopy}>
					{copied ? 'Copied' : 'Copy'}
				</Button>
				{/* Says what it does: the key is gone from the UI afterwards. */}
				<Button type="button" variant="primary" className="rounded-ds-full px-5" onPress={onClose}>
					I've saved it
				</Button>
			</div>
		</DialogContent>
	)
}
